import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'salusflow_chat_session_id';

const genSessionId = () => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `sf-${Date.now().toString(36)}-${rand}`;
};

const WELCOME = {
  role: 'assistant',
  content:
    "Hi there. I'm the SalusFlow assistant. Ask me anything about AI receptionists, WhatsApp bots, booking automation or SEO for your clinic. If you'd rather speak to a human, you can book a free consultation from the site.",
};

export const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const sessionId = useMemo(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = genSessionId();
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const send = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply || 'Sorry, no reply.' }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Sorry, I'm having trouble replying right now. Please try again in a moment or book a free consultation from the site.",
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="sf-chat-launcher"
          aria-label="Open chat"
          data-testid="ai-chat-launcher"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="sf-chat-panel" role="dialog" aria-label="SalusFlow chat" data-testid="ai-chat-panel">
          <div className="sf-chat-header">
            <div className="sf-chat-header-title">
              <div className="sf-chat-avatar" aria-hidden="true">SF</div>
              <div>
                <div className="sf-chat-heading">SalusFlow Assistant</div>
                <div className="sf-chat-sub">Usually replies instantly</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="sf-chat-close"
              aria-label="Close chat"
              data-testid="ai-chat-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="sf-chat-messages" ref={scrollRef} data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`sf-msg-row ${m.role === 'user' ? 'sf-msg-user' : 'sf-msg-bot'}`}
              >
                <div className={`sf-msg-bubble ${m.error ? 'sf-msg-error' : ''}`}>{m.content}</div>
              </div>
            ))}
            {sending && (
              <div className="sf-msg-row sf-msg-bot">
                <div className="sf-msg-bubble sf-msg-typing" aria-label="Assistant is typing">
                  <span className="sf-dot" />
                  <span className="sf-dot" />
                  <span className="sf-dot" />
                </div>
              </div>
            )}
          </div>

          <form className="sf-chat-input" onSubmit={send}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              disabled={sending}
              maxLength={2000}
              data-testid="ai-chat-input"
              aria-label="Message"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="sf-chat-send"
              data-testid="ai-chat-send"
              aria-label="Send message"
            >
              {sending ? <Loader2 className="w-5 h-5 sf-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
